import { StateCreator } from 'zustand';

import { HEALTH_FAST_QUOTE_STEPS } from '@healthquote/constants';
import insureAPIs, { healthBoundariesTransform } from '@services/apis/insure';

import {
  ProductSliceState,
  HealthQuoteStoreState,
  HealthProductType,
  HealthQuotePlanDetails
} from './types';

export const productSlice: StateCreator<
  HealthQuoteStoreState,
  [['zustand/immer', never], ['zustand/devtools', never], never],
  [],
  ProductSliceState
> = (set, get) => ({
  loadingProductBoundaries: false,
  loadingProductFeatures: false,
  productBoundariesError: '',
  productsList: [],
  paymentTermOptions: [],
  loadingPaymentTermOptions: false,
  boundariesInvalid: false,
  // fetch and store product boundaries in store
  fetchProductBoundaries: async (
    products: string[],
    tempPlan?: HealthQuotePlanDetails
  ) => {
    const plan = get().plan;
    const productsList = get().productsList;
    set((state) => {
      state.loadingProductBoundaries = true;
      state.productBoundariesError = '';
    });
    let boundariesInvalid = false;
    try {
      const productBoundariesResponse = await insureAPIs.getHealthProductBoundaries(
        healthBoundariesTransform(!tempPlan ? plan : tempPlan, products)
      );
      /**
       * when boundaries changes and we have to check whether the current sumInsured falls in
       * the list of unavailable covers in that case we should choose the next bigger cover or
       * select a valid cover from the available covers, If this happens,it also means that the
       * boundaries has to be re-fetched since boundaries are dependent on the sumInsured.
       * We do this using the boundariesInvalid state.
       */
      let sumInsured = plan.sumInsured ? plan.sumInsured : 1000000;

      if (plan.product) {
        const currentProduct = productBoundariesResponse.data.find(
          (item) => item.product.key === plan.product
        );
        if (currentProduct?.boundaries?.recommendedCover) {
          sumInsured = plan.sumInsured
            ? plan.sumInsured
            : currentProduct.boundaries.recommendedCover;
        }
        if (
          currentProduct?.boundaries.type === HealthProductType.Normal ||
          currentProduct?.boundaries.type === HealthProductType.WithTypes
        ) {
          if (
            currentProduct?.boundaries.unavailableCovers?.includes(sumInsured) ||
            !currentProduct?.boundaries.allCovers?.includes(sumInsured)
          ) {
            const higherSumInsured = currentProduct?.boundaries.allCovers?.find(
              (item) =>
                item > plan.sumInsured &&
                !currentProduct?.boundaries.unavailableCovers?.includes(item)
            );

            const validSumInsured = currentProduct?.boundaries.allCovers?.find(
              (item) =>
                item !== plan.sumInsured &&
                currentProduct?.boundaries?.unavailableCovers?.indexOf(item) === -1
            );

            // it is assumed that at least one valid cover will be preset at any point
            sumInsured = higherSumInsured ? higherSumInsured : validSumInsured!;

            // boundaries are no longer valid since sumInsured has changed
            boundariesInvalid = true;
          }
        } else {
          // in case of top-up set 0 as sum insured if getting the boundaries for the first time
          sumInsured = !productsList.length ? 0 : plan.sumInsured;
        }
      }

      set((state) => {
        state.loadingProductBoundaries = false;
        state.productsList = productBoundariesResponse.data;
        state.plan.sumInsured = sumInsured;
        state.boundariesInvalid = boundariesInvalid;
      });
    } catch (err) {
      set((state) => {
        state.loadingProductBoundaries = false;
        state.productBoundariesError =
          products.length === 1
            ? 'Failed to fetch product details'
            : 'Failed to fetch recommended plans details';
      });
    }
  },
  // fetch and store product features in store
  fetchProductFeatures: async (
    products: string[],
    tempPlan?: HealthQuotePlanDetails
  ) => {
    // proceed only if products are present
    if (products.length) {
      set((state) => {
        state.loadingProductFeatures = true;
      });

      const plan = get().plan;
      const step = get().step;

      try {
        const productFeaturesResponse = await insureAPIs.getHealthProductFeatures(
          healthBoundariesTransform({
            ...(!tempPlan ? plan : tempPlan),
            sumInsured:
              step > HEALTH_FAST_QUOTE_STEPS.MEMBERS && !tempPlan
                ? plan.sumInsured
                : tempPlan?.sumInsured ?? 0
          })
        );

        const latestProductList = [...get().productsList];

        // update existing productList with latest values
        productFeaturesResponse.data.map((productFeatures: any) => {
          const current = latestProductList.findIndex(
            (item) => item.product.key === productFeatures.product
          );
          if (current !== -1) {
            latestProductList[current] = {
              ...latestProductList[current],
              boundaries: {
                ...latestProductList[current].boundaries,
                features: productFeatures.features,
                mainBenefits: productFeatures.mainBenefits,
                extraBenefits: productFeatures.extraBenefits,
                waitingPeriods: productFeatures.waitingPeriods,
                notCovered: productFeatures.notCovered
              }
            };
          }
        });

        set((state) => {
          state.loadingProductFeatures = false;
          state.productsList = latestProductList;
        });
      } catch (err) {
        set((state) => {
          state.loadingProductFeatures = false;
          state.productBoundariesError =
            products.length === 1
              ? 'Failed to fetch product details'
              : 'Failed to fetch recommended plans details';
        });
      }
    }
  }
});
